import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Camera,
  MapPin,
  Clock,
  Info
} from "lucide-react";
import { webcams } from "../data/mock";
import WebcamPlayer from "../components/WebcamPlayer";

const WebcamPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen">
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="/assets/z9aowyx7_Umoona2025-P1043722.jpg"
            alt="Live Views of Coober Pedy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="space-y-6">
                <Badge variant="secondary" className="bg-red-500/20 text-red-300 border border-red-400/30 hover:bg-red-500/30 px-4 py-2 backdrop-blur-sm">
                  🔴 Live Streaming • Updated 24/7
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                  Live Coober Pedy
                  <span className="text-blue-400 block">
                    Webcams
                  </span>
                </h1>
                <p className="text-xl text-gray-200 leading-relaxed max-w-3xl mx-auto">
                  {webcams.description}
                </p>
              </div>
            

            </div>
          </div>
        </div>
      </section>

      {/* Live Webcam Feeds */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Live Views of Coober Pedy
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Watch our opal mining town in real-time with these live camera feeds
            </p>
          </div>

          {/* Desktop Layout - Side by Side */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-8">
            {webcams.cameras.map((camera) => (
              <Card key={camera.id} className="overflow-hidden shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-3">
                    <Camera className="w-6 h-6 text-blue-600" />
                    <span>{camera.name}</span>
                  </CardTitle>
                  <p className="text-gray-600">{camera.description}</p>
                </CardHeader>
                <CardContent className="p-0">
                  <WebcamPlayer
                    streamUrl={camera.streamUrl}
                    refreshKey={refreshKey}
                    onRefresh={handleRefresh}
                  />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile Layout - Stacked */}
          <div className="lg:hidden space-y-8">
            {webcams.cameras.map((camera) => (
              <Card key={camera.id} className="overflow-hidden shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-3">
                    <Camera className="w-6 h-6 text-blue-600" />
                    <span>{camera.name}</span>
                  </CardTitle>
                  <p className="text-gray-600">{camera.description}</p>
                </CardHeader>
                <CardContent className="p-0">
                  <WebcamPlayer
                    streamUrl={camera.streamUrl}
                    refreshKey={refreshKey}
                    onRefresh={handleRefresh}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Webcam Information */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              About Our Webcams
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-6">
              <CardHeader className="p-0 pb-4">
                <CardTitle className="flex items-center space-x-3">
                  <Clock className="w-6 h-6 text-blue-600" />
                  <span>Live Updates</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 space-y-3">
                <p className="text-gray-600">
                  Our webcams update continuously throughout the day, giving you real-time views 
                  of Coober Pedy's unique landscape and weather conditions.
                </p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Updates every few seconds</li>
                  <li>• Available 24 hours a day</li>
                  <li>• High-definition quality</li>
                  <li>• Weather-resistant cameras</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardHeader className="p-0 pb-4">
                <CardTitle className="flex items-center space-x-3">
                  <MapPin className="w-6 h-6 text-blue-600" />
                  <span>Camera Locations</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 space-y-3">
                <p className="text-gray-600">
                  Strategically positioned to capture the best views of Coober Pedy's 
                  main attractions and natural beauty.
                </p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Main Street and visitor center</li>
                  <li>• Desert landscape panorama</li>
                  <li>• Weather monitoring</li>
                  <li>• Activity around the mine</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>



      {/* Technical Information */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8 bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardContent className="space-y-4 p-0">
              <div className="flex items-center space-x-3 mb-4">
                <Info className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Webcam Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Viewing Tips</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Best viewing quality during daylight hours</li>
                    <li>• Refresh page if video doesn't load</li>
                    <li>• Allow time for feeds to initialize</li>
                    <li>• Check your internet connection for smooth playback</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Local Time</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Coober Pedy operates on Australia/Adelaide timezone
                  </p>
                  <p className="text-lg font-semibold text-blue-600">
                    {new Date().toLocaleString('en-AU', {
                      timeZone: 'Australia/Adelaide',
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready to Experience Coober Pedy in Person?
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                The webcams are just the beginning! Visit us for the full underground experience, 
                free museum access, and authentic Australian opals.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold px-8 py-4 text-lg"
              >
                <Link to="/book-tour">Book Underground Tour</Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-slate-900 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebcamPage;